import _ from 'lodash'
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '../types'


/*
 * DatumLink
 * A link between an center cutout (hole) and a shape segment or or another hole
 */
export default class DatumLink {
  constructor() {
    this.id = uuidv4()

    // Settings
    this.isValidDatumLink = true
    this.isFixedDatumLink = false

    // Source - What it's linked to and offset info
    this.sourceShapeId = null
    this.sourceDatumType = GlassDesignerTypes.DatumTypes.glassSegment
    this.sourceOffsetDistance = 0
    this.sourceOffsetDirection = 1
    this.sourceAngleOffset = 0

    // Source - Position
    this.sourcePosPoint = new THREE.Vector2()
    this.sourcePosDir = new THREE.Vector2()       // Direction from source point to this

    // Source - Glass Segment
    this.segmentId = null
    this.segmentSegmentDistanceAlong = 0

    // Source - Datum Line
    this.datumLineId = null

    // Source - Shape Edge
    this.shapeBoundsEdgeIndex = 0

    // Source - Hole
    this.sourceHoleObjectId = null
    this.sourceHoleMeasurePointHorzType = GlassDesignerTypes.HoleMeasurePointTypes.center
    this.sourceHoleMeasurePointVertType = GlassDesignerTypes.HoleMeasurePointTypes.middle
    this.sourceHoleMeasureAxisType = GlassDesignerTypes.AxisTypes.horizontal

    // Extra info
    this.userData = null
  }

  clear() {
    this.isValidDatumLink = true
    this.isFixedDatumLink = false

    this.sourceShapeId = null
    this.sourceDatumType = GlassDesignerTypes.DatumTypes.glassSegment
    this.sourceOffsetDistance = 0
    this.sourceOffsetDirection = 1
    this.sourceAngleOffset = 0

    // Source - Glass Segment
    this.segmentId = null
    this.segmentSegmentDistanceAlong = 0

    // Source - Datum Line
    this.datumLineId = null

    // Source - Shape Edge
    this.shapeBoundsEdgeIndex = 0

    // Source - Hole
    this.sourceHoleObjectId = null
    this.sourceHoleMeasurePointHorzType = GlassDesignerTypes.HoleMeasurePointTypes.center
    this.sourceHoleMeasurePointVertType = GlassDesignerTypes.HoleMeasurePointTypes.middle
    this.sourceHoleMeasureAxisType = GlassDesignerTypes.AxisTypes.horizontal

    this.userData = null
  }

  update(props) {
    Object.keys(props).forEach(key => {
      this[key] = props[key]
    })
  }

  /**
   * Calculate the 2D direction of the datum link away from the source
   */
  calculateLinkDirection(editor) {
    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegment) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let segment = glassShape.getSegmentById(this.segmentId)
      if(segment == null)
        return null

      var segOutDirection = segment.getOutDirection()

      // If on the outside of the segment, inverse it so the direction is always from source towards target
      if(this.sourceOffsetDirection < 0)
        segOutDirection.negate()

      return segOutDirection
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeCircleEdge) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let dir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(this.sourceAngleOffset))
      dir.negate()

      return dir
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeBoundsEdge) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let bounds = glassShape.calculateSegmentBounds()

      // 4 edges: top, right, bottom, left
      let edges = [
        [new THREE.Vector2(bounds.min.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.min.y)],
        [new THREE.Vector2(bounds.max.x, bounds.min.y), new THREE.Vector2(bounds.max.x, bounds.max.y)],
        [new THREE.Vector2(bounds.max.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.max.y)],
        [new THREE.Vector2(bounds.min.x, bounds.max.y), new THREE.Vector2(bounds.min.x, bounds.min.y)]
      ]
      let edgeLine = edges[this.shapeBoundsEdgeIndex]
      var dir = new THREE.Vector2()
      dir.subVectors(edgeLine[1], edgeLine[0])
      dir.normalize()
      let rightDir = new THREE.Vector2(-dir.y, dir.x)

      return rightDir
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentStart || 
      this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentEnd) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let segment = glassShape.getSegmentById(this.segmentId)
      if(segment == null)
        return null

      var segDirection = segment.getDirection()
      if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentEnd)
        segDirection.negate()

      // If on the outside of the segment, inverse it so the direction is always from source towards target
      if(this.sourceOffsetDirection < 0)
        segDirection.negate()

      return segDirection
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLine) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let datumLine = glassShape.getDatumLineById(this.datumLineId)
      if(datumLine == null)
        return null

      var lineOutDirection = datumLine.getOutDirection()

      // If on the outside of the datum line, inverse it so the direction is always from source towards target
      if(this.sourceOffsetDirection < 0)
        lineOutDirection.negate()

      return lineOutDirection
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineStart ||
       this.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineEnd) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let datumLine = glassShape.getDatumLineById(this.datumLineId)
      if(datumLine == null)
        return null

      var lineDirection = datumLine.getDirection()
      if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.datumLineEnd)
        lineDirection.negate()

      // If on the outside of the datum line, inverse it so the direction is always from source towards target
      if(this.sourceOffsetDirection < 0)
        lineDirection.negate()

      return lineDirection
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.hole) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let sourceHole = glassShape.getHoleById(this.sourceHoleObjectId)
      if(sourceHole == null)
        return null

      var outDir = new THREE.Vector2(
        this.sourceHoleMeasureAxisType == GlassDesignerTypes.AxisTypes.horizontal ? 1 : 0,
        this.sourceHoleMeasureAxisType == GlassDesignerTypes.AxisTypes.vertical ? 1 : 0
      )
      // If on the outside of the segment, inverse it so the direction is always from source towards target
      if(this.sourceOffsetDirection < 0)
        outDir.negate()

      return outDir
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.position) {
      return this.sourcePosDir.clone()
    }

    return null
  }


  /**
   * Check if the datum link has an absolute source position
   */
  hasSourcePoint() {
    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentStart || this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentEnd)
      return true
    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.position)
      return true

    return false
  }


  /**
   * Check if the datum link has an editable angle option
   */
  hasAngleOption() {
    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeCircleEdge)
      return true

    return false
  }


  /**
   * Calculate the angle of the datum link if hasAngleOption() is true
   */
  calculateSourceAngle() {
    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeCircleEdge)
      return this.sourceAngleOffset

    return 0
  }



  /**
   * Calculate the source position if available
   */
  calculateSourcePosition(editor) {
    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentStart || this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentEnd) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let segment = glassShape.getSegmentById(this.segmentId)
      if(segment == null)
        return null

      if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.glassSegmentStart)
        return segment.startPosition.clone()

      return segment.endPosition.clone()
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.shapeCircleEdge) {
      let glassShape = editor.getObjectById(this.sourceShapeId)
      if(glassShape == null)
        return null

      let bounds = glassShape.calculateSegmentBounds()
      let circleRadius = glassShape.radius
      let circleCenter = bounds.getCenter(new THREE.Vector2())
      let dir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(this.sourceAngleOffset))

      // Distance from edge of the circle
      return circleCenter.clone().addScaledVector(dir, circleRadius - this.sourceOffsetDistance)
    }

    if(this.sourceDatumType == GlassDesignerTypes.DatumTypes.position) {
      return this.sourcePosPoint.clone()
    }

    return null
  }

  // Copy a datum link to this datum link
  copy(otherDatumLink) {
    this.update({
      isValidDatumLink: otherDatumLink.isValidDatumLink,
      isFixedDatumLink: otherDatumLink.isFixedDatumLink,

      sourceShapeId: otherDatumLink.sourceShapeId,
      sourceDatumType: otherDatumLink.sourceDatumType,
      sourceOffsetDistance: otherDatumLink.sourceOffsetDistance,
      sourceOffsetDirection: otherDatumLink.sourceOffsetDirection,
      sourceAngleOffset: otherDatumLink.sourceAngleOffset,

      segmentId: otherDatumLink.segmentId,
      segmentSegmentDistanceAlong: otherDatumLink.segmentSegmentDistanceAlong,

      sourcePosPoint: otherDatumLink.sourcePosPoint.clone(),
      sourcePosDir: otherDatumLink.sourcePosDir.clone(),

      datumLineId: otherDatumLink.datumLineId,

      shapeBoundsEdgeIndex: otherDatumLink.shapeBoundsEdgeIndex,

      sourceHoleObjectId: otherDatumLink.sourceHoleObjectId,
      sourceHoleMeasurePointHorzType: otherDatumLink.sourceHoleMeasurePointHorzType,
      sourceHoleMeasurePointVertType: otherDatumLink.sourceHoleMeasurePointVertType,
      sourceHoleMeasureAxisType: otherDatumLink.sourceHoleMeasureAxisType,

      userData: _.cloneDeep(otherDatumLink.userData)
    })
  }


  // Clone this datum link
  clone() {
    var newLink = new DatumLink()
    newLink.copy(this)
    
    return newLink
  }

  // Serialize/Deserialize to/from a JSON object
  serialize() {
    return {
      id: this.id,
      class: 'DatumLink',

      isValidDatumLink: this.isValidDatumLink,
      isFixedDatumLink: this.isFixedDatumLink,
      sourceShapeId: this.sourceShapeId,
      sourceDatumType: this.sourceDatumType,
      sourceOffsetDistance: this.sourceOffsetDistance,
      sourceOffsetDirection: this.sourceOffsetDirection,
      sourceAngleOffset: this.sourceAngleOffset,

      sourcePosPoint: MathsUtils.serializeVector2(this.sourcePosPoint),
      sourcePosDir: MathsUtils.serializeVector2(this.sourcePosDir),

      segmentId: this.segmentId,
      segmentSegmentDistanceAlong: this.segmentSegmentDistanceAlong,

      datumLineId: this.datumLineId,
      shapeBoundsEdgeIndex: this.shapeBoundsEdgeIndex,
      userData: _.cloneDeep(this.userData),

      sourceHoleObjectId: this.sourceHoleObjectId,
      sourceHoleMeasurePointHorzType: this.sourceHoleMeasurePointHorzType,
      sourceHoleMeasurePointVertType: this.sourceHoleMeasurePointVertType,
      sourceHoleMeasureAxisType: this.sourceHoleMeasureAxisType
    }
  }
  deserialize(object) {
    this.id = object.id || this.id
    this.isValidDatumLink = object.isValidDatumLink
    this.isFixedDatumLink = object.isFixedDatumLink

    this.sourceShapeId = object.sourceShapeId
    this.sourceDatumType = object.sourceDatumType
    this.sourceOffsetDistance = parseFloat(object.sourceOffsetDistance)
    this.sourceOffsetDirection = parseFloat(object.sourceOffsetDirection)
    this.sourceAngleOffset = parseFloat(object.sourceAngleOffset || 0)

    this.sourcePosPoint = MathsUtils.deserializeVector2(object.sourcePosPoint)
    this.sourcePosDir = MathsUtils.deserializeVector2(object.sourcePosDir)

    this.segmentId = object.segmentId
    this.segmentSegmentDistanceAlong = parseFloat(object.segmentSegmentDistanceAlong)

    this.datumLineId = object.datumLineId
    this.shapeBoundsEdgeIndex = parseInt(object.shapeBoundsEdgeIndex || 0)
    this.userData = _.cloneDeep(object.userData)

    this.sourceHoleObjectId = object.sourceHoleObjectId
    this.sourceHoleMeasurePointHorzType = object.sourceHoleMeasurePointHorzType
    this.sourceHoleMeasurePointVertType = object.sourceHoleMeasurePointVertType
    this.sourceHoleMeasureAxisType = object.sourceHoleMeasureAxisType
  }
}
